import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminMonthlyReports } from '../hook/useAdminMonthlyReports.mjs';
import {
  getReportStatus,
  getReportStatusLabel,
  buildReportStatusFilterOptions,
} from '../../utils/statusConfig.mjs';
import PageShell from '../../components/PageShell.mjs';
import { useDialog } from '../../components/dialog/DialogProvider.mjs';



const AdminMonthlyReportDashboard = () => {
  const navigate = useNavigate();
  const { confirm, alert } = useDialog();
  const {
    reports,
    loading,
    error,
    filters,
    updateFilter,
    clearFilters,
    deleteDraftReport
  } = useAdminMonthlyReports();


  const [_selectedYear, _setSelectedYear] = useState(new Date().getFullYear());
  const [deletingId, setDeletingId] = useState(null);

  const handleViewReport = (reportId) => {
    navigate(`/monthly-report/${reportId}`);
  };

  // A draft has never been submitted, so nobody has reviewed or signed it and
  // it can go. Its claims are released and appear in the next report generated.
  const handleDeleteDraft = async (report) => {
    const monthName = new Date(report.year, report.month - 1).toLocaleString('default', { month: 'long' });
    if (!await confirm(
      `Delete the draft report for ${monthName} ${report.year}? Its claims stay approved and will be picked up by the next report you generate.`,
      { title: 'Delete draft report', confirmLabel: 'Delete draft', danger: true }
    )) return;
    setDeletingId(report._id);
    try {
      await deleteDraftReport(report._id);
    } catch (err) {
      await alert(err.message || 'The draft could not be deleted.', { title: 'Could not delete', tone: 'danger' });
    } finally {
      setDeletingId(null);
    }
  };

  // Badge colour and wording both come from the shared status config, and the
  // filter dropdown below is built from the same source. They used to be three
  // separate hand-written maps, so the dropdown offered "Pending Regional
  // Director" while the matching rows rendered "Auditor Approved", and the
  // multi-stage statuses had no colour at all.
  // Both from the REPORT vocabulary. The colour used to come from the claim
  // map, so the badge's wording and its colour disagreed - most visibly on a
  // sent-back report, which read "Sent Back for Revision" in the claim's solid
  // terminal red.
  const getStatusBadgeClass = (status) => getReportStatus(status).badge;
  const getStatusDisplayText = getReportStatusLabel;


  const generateMonthlyReport = () => {
    navigate('/generate-monthly-report')
  }

  const shellProps = { title: 'Monthly Summary Reports', subtitle: 'Manage and generate monthly expense reports' };

  if (loading) {
    return (
      <PageShell {...shellProps}>
        <div className="bg-white rounded-xl shadow-lg py-16 flex flex-col items-center justify-center">
          <div className="w-10 h-10 border-4 border-gray-200 border-t-stanford-red rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600">Loading monthly reports data...</p>
        </div>
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell {...shellProps}>
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg">
          Error: {error}
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell {...shellProps}>
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex items-center gap-2 text-gray-700 font-semibold mb-4">
            <svg className="w-5 h-5 text-stanford-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            Filters & Actions
          </div>
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-3 sm:gap-4">
            <select
              value={filters.year}
              onChange={(e) => updateFilter('year', parseInt(e.target.value))}
              className="min-h-[44px] sm:min-h-0 px-4 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-stanford-red focus:border-transparent"
            >
              {[...Array(5)].map((_, i) => (
                <option key={i} value={new Date().getFullYear() - i}>
                  {new Date().getFullYear() - i}
                </option>
              ))}
            </select>

            <select
              value={filters.status}
              onChange={(e) => updateFilter('status', e.target.value)}
              className="min-h-[44px] sm:min-h-0 px-4 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-stanford-red focus:border-transparent"
            >
              <option value="all">All Statuses</option>
              {buildReportStatusFilterOptions().map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>

            <button
              className="col-span-2 sm:col-span-1 min-h-[44px] sm:min-h-0 justify-center px-4 py-2 bg-stanford-red text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
              onClick={generateMonthlyReport}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Generate Monthly Report
            </button>
            <button
              onClick={clearFilters}
              className="col-span-2 sm:col-span-1 min-h-[44px] sm:min-h-0 justify-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              Clear filters
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full responsive-table">
              <thead className="bg-stanford-red text-white">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Month</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Year</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Region</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Date Generated</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Total Amount</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((report) => (
                  <tr key={report._id} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-700" data-cell="title">
                      {new Date(report.year, report.month - 1).toLocaleString('default', { month: 'long' })}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700" data-label="Year">{report.year}</td>
                    <td className="px-4 py-3 text-sm text-gray-700" data-label="Region">{report.region}</td>
                    <td className="px-4 py-3" data-label="Status">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeClass(report.status)}`}>
                        {getStatusDisplayText(report.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700" data-label="Generated">{new Date(report.createdAt).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 font-medium tabular-nums" data-label="Total">${(Number(report.totalAmount) || 0).toFixed(2)}</td>
                    <td className="px-4 py-3" data-cell="action">
                      <div className="flex flex-wrap gap-2">
                        <button
                          className="px-4 py-2 text-sm text-stanford-red border border-stanford-red rounded-lg hover:bg-stanford-red hover:text-white transition-colors"
                          onClick={() => handleViewReport(report._id)}
                        >
                          View Details
                        </button>
                        {report.status === 'draft' && (
                          <button
                            className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-red-50 hover:text-red-700 hover:border-red-300 transition-colors disabled:opacity-50"
                            onClick={() => handleDeleteDraft(report)}
                            disabled={deletingId === report._id}
                          >
                            {deletingId === report._id ? 'Deleting…' : 'Delete Draft'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {reports.length === 0 && (
            <div className="p-8 text-center">
              <p className="text-gray-500">No reports found for the selected filters.</p>
            </div>
          )}
        </div>
    </PageShell>
  );
};

export default AdminMonthlyReportDashboard;
